import Link from '@liveauctioneers/caterwaul-components/lib/Link/Link';
import { H2, H3 } from '@liveauctioneers/hammer-ui-core/text';
import * as React from 'react';
import styled, { useTheme } from 'styled-components';

import TopBadge from '@/components/Badges/TopBadge';
import useIsNarrowerThan from '@/hooks/useIsNarrowerThan';
import { useAppSelector } from '@/redux/hooks';
import { getSellerLogoId, getSellerName } from '@/redux/modules/seller';
import { isTopRatedHouse } from '@/redux/modules/topRatedSellers';
import { getIsWhiteLabel } from '@/redux/modules/whiteLabel';
import { getInitialWidth } from '@/redux/modules/windowSize';
import { getAuctioneerLogoUrl, getSellerUrl } from '@/utils/urls';

type Props = {
    className?: string;
    sellerId: number;
};

const SellerInfo = ({ className, sellerId }: Props) => {
    const {
        breakpoints: { tabletNarrowWidthNum },
    } = useTheme();

    const sellerName = useAppSelector((state) => getSellerName(state, sellerId));
    const sellerIsTopRated = useAppSelector((state) => isTopRatedHouse(state, sellerId));
    const logoId = useAppSelector((state) => getSellerLogoId(state, sellerId));
    const whiteLabel = useAppSelector(getIsWhiteLabel);
    const ssrWidth = useAppSelector(getInitialWidth);
    const isMobile = useIsNarrowerThan(tabletNarrowWidthNum + 1, ssrWidth);
    const showSellerLogoLink = Boolean(logoId) && !whiteLabel && !isMobile;

    return (
        <SellerSection
            className={className}
            data-testid="catalog-seller-info"
        >
            {showSellerLogoLink && (
                <StyledLogoLink
                    data-testid="seller-info-logo"
                    route
                    to={getSellerUrl(sellerId, sellerName)}
                >
                    <StyledSellerLogoImage
                        alt={sellerName}
                        height={32}
                        src={getAuctioneerLogoUrl(logoId, 80)}
                        width={32}
                    />
                </StyledLogoLink>
            )}
            <SellerAndBadge>
                <StyledLink
                    route
                    to={getSellerUrl(sellerId, sellerName)}
                >
                    {isMobile ? (
                        <H2 data-testid="h2-seller-name">{sellerName}</H2>
                    ) : (
                        <H3 data-testid="h3-seller-name">{sellerName}</H3>
                    )}
                </StyledLink>
                {sellerIsTopRated && <StyledTopBadge iconSize="small" />}
            </SellerAndBadge>
        </SellerSection>
    );
};

export default SellerInfo;

const SellerSection = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-start;
    margin-bottom: 16px;
    @media (max-width: ${({ theme }) => theme.breakpoints.tabletNarrowWidth}) {
        justify-content: center;
    }
`;

const StyledTopBadge = styled(TopBadge)`
    vertical-align: middle;
    margin-left: 8px;

    @media (max-width: ${({ theme }) => theme.breakpoints.tabletNarrowWidth}) {
        margin-left: 0;
        display: inline;
    }
`;

const StyledLink = styled(Link)`
    && {
        cursor: pointer;
        display: flex;
        text-decoration: none;

        @media (max-width: ${({ theme }) => theme.breakpoints.tabletNarrowWidth}) {
            text-align: center;
            flex-direction: column;
            margin-bottom: 16px;
        }
    }
`;

const SellerAndBadge = styled.div`
    display: flex;

    @media (min-width: ${({ theme }) => theme.breakpoints.tabletNarrowWidthNum + 1}px) {
        margin-top: 6.5px;
    }

    @media (max-width: ${({ theme }) => theme.breakpoints.tabletNarrowWidth}) {
        justify-content: center;
        margin-top: 5px;
        margin-left: 0;
        display: inline;
    }

    & > div {
        justify-content: center;
        display: flex;
    }
`;

const StyledSellerLogoImage = styled.img`
    max-width: 32px;
    max-height: 32px;
    object-fit: contain;
`;

const StyledLogoLink = styled(Link)`
    width: 38px;
    height: 38px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-right: 16px;
    border: 1px solid ${({ theme }) => theme.colors.grey400};
    background-color: ${({ theme }) => theme.colors.white};

    @media (max-width: ${({ theme }) => theme.breakpoints.tabletNarrowWidth}) {
        display: none;
    }
`;
